import type {
  Calendar,
  EffectivePriorityCtx,
  RecurrenceOverride,
  RecurrenceRule,
  Task,
  ThemeConfig,
  Topic,
} from "@app/core";
import {
  addDays,
  effectiveMatrixDisplayPos,
  effectivePriorityOf,
  endOfDay,
  fromIso,
  matrixCellCenter,
  matrixEnabledFor,
  matrixRowForPriority,
  matrixScore,
  resolveThemeColors,
  startOfDay,
  startOfWeek,
} from "@app/core";

import { translate, type AppLanguage } from "../../lib/language.js";
import { formatHm, formatHmDate } from "../../lib/format.js";
import { tasksForList, type NextInstanceCache } from "../../routes/tasks/next-instances.js";
import type { MultiDayFocus } from "../../stores/settings.store.js";
import { buildAgenda, taskSlotEnd } from "../agenda/agenda.js";
import { bucketDayCols, expandTasksWindowed, type RuleExpansionCache } from "../calendar-view/build-days.js";
import { resolvedTaskContextColor } from "../entity-colors.js";
import { contrastShade, matrixAccentFromPos, pastel, PRIORITY_FLAG_HEX } from "../task-colors.js";

/** Bump when the JSON shape changes; the native parser ignores unknown versions. */
export const WIDGET_SNAPSHOT_VERSION = 3;
/** Future days the snapshot always covers past today, so widgets stay correct
 *  across several days of the app not being opened (the native side re-selects
 *  "today" from the window on its own refresh cycle). Every month grid that can
 *  become current during that freshness window is covered in full. */
export const WIDGET_FUTURE_DAYS = 14;
const MONTH_GRID_DAYS = 42;
const MAX_ITEMS_PER_DAY = 20;
const MAX_TIMED_ITEMS_PER_DAY = 40;
const MAX_FOCUS_ITEMS = 3;
const MAX_TITLE_CODE_POINTS = 160;

// Fill softening mirrors the mobile block look (mobile-block-style.ts) so a
// widget row reads in the same color as the same entry on the in-app calendar.
const FILL_SOFT = 0.55;

export interface WidgetSnapshotItem {
  /** Task id — virtual occurrence ids included; the deep-link target. */
  id: string;
  title: string;
  /** Localized clock label ("" for all-day entries). */
  time: string;
  /** Occurrence start in epoch ms (all-day → local midnight of its day). */
  startMs: number;
  /** Occupied-slot end in epoch ms (see agenda's `taskSlotEnd`). */
  endMs: number;
  allDay: boolean;
  /** Row fill hex (pastel-softened entry color). */
  fill: string;
  /** Readable ink hex on `fill`. */
  ink: string;
  /** Priority accent hex ("" when none) — continuous matrix color in scope. */
  accent: string;
  done: boolean;
  snoozed: boolean;
  isEvent: boolean;
}

export interface WidgetSnapshotDay {
  /** Local calendar date, `yyyy-MM-dd` — the native side matches on this. */
  date: string;
  /** Localized short label, e.g. "Sat, Jul 19". */
  label: string;
  /** Start-day items for the Agenda and month widgets. */
  items: WidgetSnapshotItem[];
  /** Items whose occupied slot intersects this day, including overnight
   * continuations. Used by timeline widgets and Next up. */
  timelineItems: WidgetSnapshotItem[];
}

export interface WidgetFocusItem {
  id: string;
  title: string;
  accent: string;
  fill: string;
  ink: string;
  /** Localized short deadline/schedule label ("" when undated). */
  when: string;
}

export interface WidgetSnapshotTheme {
  dark: boolean;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  accent: string;
  onAccent: string;
  outline: string;
}

export interface WidgetSnapshot {
  version: number;
  generatedAt: string;
  theme: WidgetSnapshotTheme;
  /** Pre-localized UI strings so native widgets follow the APP language. */
  strings: Record<string, string>;
  days: WidgetSnapshotDay[];
  focus: WidgetFocusItem[];
  overdueCount: number;
  /** Number of days the Agenda widget scans, mirroring the Today horizon. */
  agendaDays: number;
  /** Calendar-view widget config/labels (all localized to the APP language). */
  weekStartsOn: 0 | 1;
  /** Left edge of the week/3-day widgets (mirrors the app's `multiDayFocus`). */
  multiDayFocus: MultiDayFocus;
  /** Short weekday names indexed by JS `Date.getDay()` (0 = Sunday). */
  weekdays: string[];
  /** Month names indexed 0–11. */
  months: string[];
  /** Hour-axis labels for hours 0–23, honoring the 12/24h setting. */
  hourLabels: string[];
}

export interface WidgetSnapshotDeps {
  tasks: Task[];
  rules: Record<string, RecurrenceRule>;
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>;
  topics: readonly Topic[];
  calendars: readonly Calendar[];
  theme: ThemeConfig;
  dark: boolean;
  language: AppLanguage;
  locale: string;
  hour12: boolean;
  weekStartsOn: 0 | 1;
  multiDayFocus: MultiDayFocus;
  agendaDays: number;
  priorityCtx: EffectivePriorityCtx;
  now: Date;
  cache?: RuleExpansionCache;
  nextCache?: NextInstanceCache;
}

function localDateKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isSnoozed(task: Task): boolean {
  return task.status === "snoozed";
}

function displayTitle(task: Task, language: AppLanguage): string {
  const title = task.title.trim() ? task.title : translate(language, "agenda.untitled");
  const points = Array.from(title);
  return points.length <= MAX_TITLE_CODE_POINTS
    ? title
    : `${points.slice(0, MAX_TITLE_CODE_POINTS - 1).join("")}…`;
}

/** Priority accent hex for a task: the continuous matrix-diagonal color for a
 *  matrix-PLACED task in scope (same placed-gate as `effectivePriorityOf`),
 *  else the discrete flag hue. "" when the effective priority is none. */
function accentOf(task: Task, ctx: EffectivePriorityCtx): string {
  if (task.matrix_pos && matrixEnabledFor(ctx.matrixScope, task.is_event)) {
    const display = effectiveMatrixDisplayPos(
      task.matrix_pos, task.priority, task.deadline_at ?? task.due_at, ctx.now, task.tz ?? ctx.defaultTz, ctx.config,
    );
    const hex = matrixAccentFromPos(display);
    if (hex) return hex;
  }
  return PRIORITY_FLAG_HEX[effectivePriorityOf(task, ctx)] || "";
}

function toItem(task: Task, deps: WidgetSnapshotDeps): WidgetSnapshotItem {
  const start = task.due_at ? fromIso(task.due_at) : deps.now;
  const end = taskSlotEnd(task) ?? start;
  const context = resolvedTaskContextColor(deps.theme, deps.topics, deps.calendars, task);
  const fill = pastel(task.custom_color ?? context, FILL_SOFT);
  return {
    id: task.id,
    title: displayTitle(task, deps.language),
    time: task.all_day ? "" : formatHmDate(start, deps.hour12),
    startMs: task.all_day ? startOfDay(start).getTime() : start.getTime(),
    endMs: end.getTime(),
    allDay: task.all_day,
    fill,
    ink: contrastShade(fill),
    accent: accentOf(task, deps.priorityCtx),
    done: task.status === "done",
    snoozed: isSnoozed(task),
    isEvent: !!task.is_event,
  };
}

function focusScore(task: Task, ctx: EffectivePriorityCtx): number | null {
  if (task.matrix_pos && matrixEnabledFor(ctx.matrixScope, task.is_event)) {
    const display = effectiveMatrixDisplayPos(
      task.matrix_pos, task.priority, task.deadline_at ?? task.due_at, ctx.now, task.tz ?? ctx.defaultTz, ctx.config,
    );
    if (display) return matrixScore(display);
  }
  const row = matrixRowForPriority(effectivePriorityOf(task, ctx));
  return row === null ? null : matrixCellCenter(row);
}

function compareIsoInstants(a: string | null, b: string | null): number {
  const aMs = a ? fromIso(a).getTime() : Number.POSITIVE_INFINITY;
  const bMs = b ? fromIso(b).getTime() : Number.POSITIVE_INFINITY;
  const safeA = Number.isFinite(aMs) ? aMs : Number.POSITIVE_INFINITY;
  const safeB = Number.isFinite(bMs) ? bMs : Number.POSITIVE_INFINITY;
  return safeA === safeB ? 0 : safeA < safeB ? -1 : 1;
}

/** The Priorities view's decision order for the focus widget: live diagonal
 * score, nearer deadline/schedule, shorter duration, then title. */
function focusSort(a: Task, b: Task, ctx: EffectivePriorityCtx): number {
  const score = (focusScore(b, ctx) ?? -1) - (focusScore(a, ctx) ?? -1);
  if (score !== 0) return score;
  const aWhen = a.deadline_at ?? a.due_at;
  const bWhen = b.deadline_at ?? b.due_at;
  const byWhen = compareIsoInstants(aWhen, bWhen);
  if (byWhen !== 0) return byWhen;
  const duration = a.duration_minutes - b.duration_minutes;
  if (duration !== 0) return duration;
  return a.title.localeCompare(b.title);
}

function orderedItems(tasks: ReadonlyArray<Task>, deps: WidgetSnapshotDeps): WidgetSnapshotItem[] {
  return [...tasks]
    .sort((a, b) => {
      if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
      const due = compareIsoInstants(a.due_at, b.due_at);
      return due !== 0 ? due : a.id.localeCompare(b.id);
    })
    .slice(0, MAX_ITEMS_PER_DAY)
    .map((task) => toItem(task, deps));
}

function orderedTimelineItems(tasks: ReadonlyArray<Task>, deps: WidgetSnapshotDeps): WidgetSnapshotItem[] {
  const ordered = [...tasks].sort((a, b) => {
    const due = compareIsoInstants(a.due_at, b.due_at);
    return due !== 0 ? due : a.id.localeCompare(b.id);
  });
  // A crowded all-day lane must not consume the cap and hide every timed
  // block (the renderer itself shows at most three all-day chips).
  return [
    ...ordered.filter((task) => task.all_day).slice(0, 3),
    ...ordered.filter((task) => !task.all_day).slice(0, MAX_TIMED_ITEMS_PER_DAY),
  ].map((task) => toItem(task, deps));
}

/**
 * Build the JSON snapshot the Android home-screen widgets render from. Pure —
 * time, stores, and theme all arrive as arguments; the caller (the
 * `useWidgetSnapshotSync` hook) owns reading them and pushing the result over
 * the WidgetBridge plugin. Hidden calendars are filtered out to match every
 * other task-listing surface; sidebar filters intentionally do NOT apply
 * (widgets are an ambient surface, not a filtered view).
 */
export function buildWidgetSnapshot(deps: WidgetSnapshotDeps): WidgetSnapshot {
  const { now, language, locale } = deps;
  const hiddenIds = new Set(deps.calendars.filter((c) => c.hidden).map((c) => c.id));
  const tasks = deps.tasks.filter((t) => !t.deleted_at && (!t.calendar_id || !hiddenIds.has(t.calendar_id)));

  // Window: the current month grid's first cell through the LAST cell of the
  // six-week grid containing today + WIDGET_FUTURE_DAYS. Covering only the
  // future date itself made the Month widget go partially blank immediately
  // after a month rollover, while the rest of the snapshot was still fresh.
  const today = startOfDay(now);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthGridStart = startOfDay(startOfWeek(monthStart, { weekStartsOn: deps.weekStartsOn }));
  // The "current day - 1" multi-day focus needs yesterday even when today is
  // the first cell of the current month grid (for example, a Monday month
  // start with Monday as week start). Without this one-day extension, the
  // first column of Week/3-day widgets is blank on exactly that boundary.
  const focusStart = deps.multiDayFocus === "current_day_minus_one" ? addDays(today, -1) : today;
  const from = monthGridStart < focusStart ? monthGridStart : focusStart;
  const futureEdge = addDays(today, WIDGET_FUTURE_DAYS);
  const futureMonthStart = new Date(futureEdge.getFullYear(), futureEdge.getMonth(), 1);
  const futureGridStart = startOfDay(startOfWeek(futureMonthStart, { weekStartsOn: deps.weekStartsOn }));
  const coverageEnd = addDays(futureGridStart, MONTH_GRID_DAYS - 1);
  const localDayOrdinal = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
  const dayCount = Math.max(MONTH_GRID_DAYS, localDayOrdinal(coverageEnd) - localDayOrdinal(from) + 1);
  const to = endOfDay(addDays(from, dayCount - 1));
  const expanded = expandTasksWindowed(tasks, deps.rules, deps.overrides, from, to, deps.cache);
  const cols = bucketDayCols(expanded, from, dayCount);
  const timelineExpanded = expandTasksWindowed(tasks, deps.rules, deps.overrides, from, to, deps.cache, true);
  const timelineCols = bucketDayCols(timelineExpanded, from, dayCount, true);

  const dayLabel = new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short" });
  const days: WidgetSnapshotDay[] = cols.map((col, index) => ({
    date: localDateKey(col.date),
    label: dayLabel.format(col.date),
    items: orderedItems(col.tasks, deps),
    timelineItems: orderedTimelineItems(timelineCols[index]!.tasks, deps),
  }));

  // Focus list: open, unsnoozed work with a priority signal. Use the same
  // two-year next-instance path as Priorities/All Tasks so sparse recurring
  // series do not disappear merely because the paint snapshot is shorter.
  // Start at today's boundary to keep a missed occurrence from earlier today.
  const ctx = deps.priorityCtx;
  // `matrix_hidden` is the same "not a decision" flag the Priorities board
  // honours — the focus widget IS that board's top of queue, so it must agree,
  // including that hiding does not apply at all while the matrix scope is off
  // (no UI exists to clear the flag there).
  const hidingActive = ctx.matrixScope !== "off";
  const eligible = (t: Task) =>
    t.status === "open" && !isSnoozed(t) && !(t.matrix_hidden && hidingActive) &&
    (!t.is_event || ctx.matrixScope === "tasks_events") &&
    focusScore(t, ctx) !== null;
  const focusPool = tasksForList(
    tasks,
    deps.rules,
    deps.overrides,
    today,
    deps.nextCache,
    true,
  ).filter(eligible).sort((a, b) => focusSort(a, b, ctx));

  const whenLabel = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
  const focus: WidgetFocusItem[] = focusPool.slice(0, MAX_FOCUS_ITEMS).map((t) => {
    const context = resolvedTaskContextColor(deps.theme, deps.topics, deps.calendars, t);
    const fill = pastel(t.custom_color ?? context, FILL_SOFT);
    const when = t.deadline_at ?? t.due_at;
    return {
      id: t.id,
      title: displayTitle(t, deps.language),
      accent: accentOf(t, ctx),
      fill,
      ink: contrastShade(fill),
      when: when ? whenLabel.format(fromIso(when)) : "",
    };
  });

  const overdueCount = buildAgenda(tasks, deps.rules, deps.overrides, now, deps.cache, 1).overdue.length;

  const roles = resolveThemeColors(deps.theme, deps.dark ? "dark" : "light");
  const t = (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) => translate(language, key, vars);

  // Localized calendar labels for the native month/week/day renderers. The
  // anchor dates are arbitrary known weekdays/months (2026-06-07 is a Sunday).
  const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const weekdays = Array.from({ length: 7 }, (_, i) => weekdayFormat.format(new Date(2026, 5, 7 + i)));
  const monthFormat = new Intl.DateTimeFormat(locale, { month: "long" });
  const months = Array.from({ length: 12 }, (_, i) => monthFormat.format(new Date(2026, i, 1)));
  const hourLabels = Array.from({ length: 24 }, (_, h) => formatHm(h, 0, deps.hour12));

  return {
    version: WIDGET_SNAPSHOT_VERSION,
    generatedAt: now.toISOString(),
    theme: {
      dark: deps.dark,
      background: roles.surfaceContainerLow,
      surface: roles.surfaceContainerHigh,
      text: roles.onSurface,
      textMuted: roles.onSurfaceVariant,
      accent: roles.primary,
      onAccent: roles.onPrimary,
      outline: roles.outlineVariant,
    },
    strings: {
      today: t("today.relative.today"),
      tomorrow: t("today.relative.tomorrow"),
      empty: t("today.emptyDay"),
      allDay: t("common.allDay"),
      done: t("common.done"),
      nextUp: t("today.upNext"),
      nextUpEmpty: t("widget.nextUpEmpty"),
      focusTitle: t("widget.focusTitle"),
      focusEmpty: t("widget.focusEmpty"),
      overdue: t("widget.overdue", { count: overdueCount }),
      newEntry: t("common.new"),
    },
    days,
    focus,
    overdueCount,
    agendaDays: deps.agendaDays,
    weekStartsOn: deps.weekStartsOn,
    multiDayFocus: deps.multiDayFocus,
    weekdays,
    months,
    hourLabels,
  };
}
